// src/components/folder/FolderList.tsx
import React, { useEffect, useMemo, useState } from 'react';

import {
  Box,
  Text,
} from '@chakra-ui/react';

import { motion } from 'framer-motion';

import PaginatedList from '../common/PaginatedList';
import { useProjectsMock, useSamples } from '../../api/projectApi';
import { Project } from '../../types/project';
import { PaginationProvider } from '../../contexts/PaginationContext';
import SampleTable from '../sample/SampleTable';

const SOFT_DELETE_KEY = 'softDeletedProjectIds';
const JOB_RUNS_KEY = 'userJobRuns';
const SAMPLE_GONE_KEY = 'sampleAutoRemoved';

const loadSoftDeleted = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(SOFT_DELETE_KEY) || '[]');
  } catch {
    return [];
  }
};
const saveSoftDeleted = (ids: string[]) =>
  localStorage.setItem(SOFT_DELETE_KEY, JSON.stringify(ids));

const getJobRuns = (): number => {
  const n = Number(localStorage.getItem(JOB_RUNS_KEY) || '0');
  return Number.isFinite(n) ? n : 0;
};
const markSampleAutoRemoved = () => localStorage.setItem(SAMPLE_GONE_KEY, 'true');
const isSampleAutoRemoved = () => localStorage.getItem(SAMPLE_GONE_KEY) === 'true';

const SAMPLE_PROJECT_ID = 'sample-001';
const SAMPLE_PROJECT_NAME = 'Jobs';

const ProjectList: React.FC<{ currentPage?: number; itemsPerPage?: number }> = () => {
  const { projects, isLoading, error } = useProjectsMock();
  const [softDeleted, setSoftDeleted] = useState<string[]>(loadSoftDeleted());



  const sampleProject: Project | null = useMemo(() => {
    if (!projects || projects.length === 0) return null;
    const base = projects[0];
    return { ...base, id: SAMPLE_PROJECT_ID, name: SAMPLE_PROJECT_NAME };
  }, [projects]);

  useEffect(() => {
    if (isSampleAutoRemoved()) return;
    if (getJobRuns() >= 2) {
      setSoftDeleted((prev) => {
        if (prev.includes(SAMPLE_PROJECT_ID)) return prev;
        const next = [...prev, SAMPLE_PROJECT_ID];
        saveSoftDeleted(next);
        return next;
      });
      markSampleAutoRemoved();
    }
  }, []);

  const filteredProjects: Project[] = useMemo(() => {
    if (!sampleProject) return [];
    if (softDeleted.includes(SAMPLE_PROJECT_ID)) return [];
    return [sampleProject];
  }, [sampleProject, softDeleted]);

  if (isLoading) return <Text>Loading...</Text>;
  if (error) return <Text color="red.500">{error}</Text>;

  return (
    <PaginationProvider data={filteredProjects} initialPageSize={1000}>
      <Box w="100%" overflowY="auto" maxH="calc(100vh - 350px)" pr={2}>
        <PaginatedListContent />
      </Box>
    </PaginationProvider>
  );
};

const PaginatedListContent: React.FC = () => {
  const renderProject = (project: Project) => {
    const realProjectId = project.id;

    return (
      <motion.div
        key={project.id}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Box>
          <SampleDetails projectIdToFetch={realProjectId} />
        </Box>
      </motion.div>
    );
  };

  return <PaginatedList<Project> renderItem={renderProject} />;
};

const SampleDetails: React.FC<{ projectIdToFetch: string }> = ({ projectIdToFetch }) => {
  const { samples, isLoading, error } = useSamples(projectIdToFetch);

  if (isLoading) return <Text mt={2}>Loading samples...</Text>;
  if (error) return <Text mt={2} color="red.500">{error}</Text>;
  if (!samples.length) return <Text mt={2}>No samples found.</Text>;

  const normalized = samples.map((s: any) => ({
    id: s.job_id ?? s.id,
    job_id: s.job_id,
    name: s.name ?? s.sample_name,
    analysis: s.analysis,
    date: s.date,
    type: s.type ?? s.dataType ?? s.data_type ?? 'BAM File',
    stage: s.stage,
    progress: s.progress,
    status: s.status,
  }));

  return (
    <Box mt={4} border="1px solid" borderColor="surface.300" borderRadius="16px" p={2} bg="white">
      <SampleTable samples={normalized} />
    </Box>
  );
};

export default ProjectList;
